import { useCallback, useMemo } from 'react';
import { create } from 'zustand';

import {
  type TransferReviewBatch,
  TransferReviewBatchDecision,
  approvalsGetTransferReviewBatchOptions,
} from '@column/api';
import { formatNumber } from '@column/column-ui-kit';

import { approvalListsFilter } from '~/lib/api/approvals';
import { queryClient } from '~/lib/api/queryClient';
import { pluralizeTransfers } from '~/lib/transfers/utils';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';

export const REVIEW_BATCH_POLL_MS = 1_500;
const JOB_TIMEOUT_MS = 10 * 60_000;
const MAX_POLL_FAILURES = 3;
const REVIEWED_GRACE_MS = 60_000;

interface ReviewBatchJob {
  id: string;
  userId: string;
  platformId: string;
  isSandbox: boolean;
  targetIds: string[];
  submittedAt: number;
}

interface ReviewBatchJobsState {
  jobs: ReviewBatchJob[];
  hiddenIds: ReadonlySet<string>;
  hide: (ids: string[]) => void;
  unhide: (ids: string[]) => void;
  markReviewed: (ids: string[]) => void;
  addJob: (job: ReviewBatchJob) => void;
  removeJob: (id: string) => void;
}

export const isReviewBatchDone = (batch: TransferReviewBatch | undefined): boolean =>
  batch?.status === 'completed' || batch?.status === 'failed';

const invalidateApprovalLists = () => queryClient.invalidateQueries(approvalListsFilter);

const notifyOutcome = (batch: TransferReviewBatch | undefined) => {
  const { addDangerNotification, addSuccessNotification, addWarningNotification } = useNotificationStore.getState();
  if (!batch) {
    addWarningNotification({ content: 'A transfer review is still processing. Check back later for results.' });
    return;
  }
  const verb = batch.decision === TransferReviewBatchDecision.APPROVED ? 'approved' : 'rejected';
  const details = [
    batch.skipped_count > 0 ? `${formatNumber(batch.skipped_count, false)} skipped` : undefined,
    batch.failed_count > 0 ? `${formatNumber(batch.failed_count, false)} failed` : undefined,
  ].filter(Boolean);
  const content = `${pluralizeTransfers(batch.succeeded_count)} ${verb}${details.length > 0 ? ` (${details.join(', ')})` : ''}`;
  if (batch.status === 'failed' || batch.failed_count > 0) addDangerNotification({ content });
  else addSuccessNotification({ content });
};

const REVIEWED_RECORD_STATUSES = new Set(['approved', 'rejected']);
const UNREVIEWED_RECORD_STATUSES = new Set(['failed', 'skipped']);

const getReviewedIds = (batch: TransferReviewBatch, targetIds: string[]): string[] => {
  const { records } = batch;
  const reviewed = records
    .filter((record) => REVIEWED_RECORD_STATUSES.has(record.status))
    .map((record) => record.transfer_id);
  const allReviewed = records.length > 0 && reviewed.length === records.length;
  return allReviewed ? [...new Set([...reviewed, ...targetIds])] : reviewed;
};

const watched = new Set<string>();
const reviewedUntil = new Map<string, number>();

const activeScope = () => {
  const { currentUser, currentPlatform, isSandbox } = useSessionStore.getState();
  return currentUser?.id && currentPlatform?.id
    ? { userId: currentUser.id, platformId: currentPlatform.id, isSandbox: Boolean(isSandbox) }
    : undefined;
};

const isInScope = (job: ReviewBatchJob): boolean => {
  const scope = activeScope();
  return (
    !!scope && job.userId === scope.userId && job.platformId === scope.platformId && job.isSandbox === scope.isSandbox
  );
};

const watchJob = (job: ReviewBatchJob) => {
  if (watched.has(job.id) || !isInScope(job)) return;
  watched.add(job.id);

  const stop = (batch?: TransferReviewBatch, notify = true) => {
    watched.delete(job.id);
    const { removeJob, markReviewed, unhide } = useReviewBatchJobsStore.getState();
    const reviewedIds = batch ? getReviewedIds(batch, job.targetIds) : [];
    markReviewed(reviewedIds);
    unhide(job.targetIds.filter((id) => !reviewedIds.includes(id)));
    removeJob(job.id);
    if (notify) notifyOutcome(batch);
    invalidateApprovalLists();
  };

  let failures = 0;
  const poll = async () => {
    const stillTracked = useReviewBatchJobsStore.getState().jobs.some((existing) => existing.id === job.id);
    if (!stillTracked || !isInScope(job)) {
      watched.delete(job.id);
      return;
    }
    try {
      const batch = await queryClient.fetchQuery({
        ...approvalsGetTransferReviewBatchOptions({ path: { transfer_review_batch_id: job.id } }),
        staleTime: 0,
      });
      failures = 0;
      useReviewBatchJobsStore
        .getState()
        .unhide(
          batch.records
            .filter((record) => UNREVIEWED_RECORD_STATUSES.has(record.status))
            .map((record) => record.transfer_id)
        );
      if (isReviewBatchDone(batch)) {
        stop(batch);
        return;
      }
      if (Date.now() - job.submittedAt > JOB_TIMEOUT_MS) {
        stop(undefined);
        return;
      }
    } catch {
      failures += 1;
      if (failures > MAX_POLL_FAILURES) {
        stop(undefined, false);
        return;
      }
    }
    window.setTimeout(poll, REVIEW_BATCH_POLL_MS);
  };
  void poll();
};

export const useReviewBatchJobsStore = create<ReviewBatchJobsState>()((set) => ({
  jobs: [],
  hiddenIds: new Set(),
  hide: (ids) => {
    if (ids.length === 0) return;
    set((state) => ({ hiddenIds: new Set([...state.hiddenIds, ...ids]) }));
  },
  unhide: (ids) => {
    const shown = new Set(ids.filter((id) => useReviewBatchJobsStore.getState().hiddenIds.has(id)));
    if (shown.size === 0) return;
    shown.forEach((id) => reviewedUntil.delete(id));
    set((state) => ({ hiddenIds: new Set([...state.hiddenIds].filter((id) => !shown.has(id))) }));
  },
  markReviewed: (ids) => {
    if (ids.length === 0) return;
    const expiresAt = Date.now() + REVIEWED_GRACE_MS;
    ids.forEach((id) => reviewedUntil.set(id, expiresAt));
    set((state) => ({ hiddenIds: new Set([...state.hiddenIds, ...ids]) }));
    window.setTimeout(() => {
      const expired = ids.filter((id) => (reviewedUntil.get(id) ?? Infinity) <= Date.now());
      if (expired.length === 0) return;
      useReviewBatchJobsStore.getState().unhide(expired);
      invalidateApprovalLists();
    }, REVIEWED_GRACE_MS);
  },
  addJob: (job) => {
    set((state) => ({ jobs: [...state.jobs.filter((existing) => existing.id !== job.id), job] }));
    watchJob(job);
  },
  removeJob: (id) => set((state) => ({ jobs: state.jobs.filter((job) => job.id !== id) })),
}));

const watchScopedJobs = () => useReviewBatchJobsStore.getState().jobs.forEach(watchJob);

useSessionStore.subscribe(watchScopedJobs);

const useReviewBatchJobScope = () => {
  const userId = useSessionStore((state) => state.currentUser?.id);
  const platformId = useSessionStore((state) => state.currentPlatform?.id);
  const isSandbox = Boolean(useSessionStore((state) => state.isSandbox));
  return useMemo(
    () => (userId && platformId ? { userId, platformId, isSandbox } : undefined),
    [userId, platformId, isSandbox]
  );
};

const useActiveReviewBatchJobs = (): ReviewBatchJob[] => {
  const scope = useReviewBatchJobScope();
  const jobs = useReviewBatchJobsStore((state) => state.jobs);
  return useMemo(
    () =>
      scope
        ? jobs.filter(
            (job) =>
              job.userId === scope.userId && job.platformId === scope.platformId && job.isSandbox === scope.isSandbox
          )
        : [],
    [jobs, scope]
  );
};

export const useInReviewTransferIds = (): ReadonlySet<string> => {
  const jobs = useActiveReviewBatchJobs();
  return useMemo(() => new Set(jobs.flatMap((job) => job.targetIds)), [jobs]);
};

export const useTrackReviewBatchJob = () => {
  const scope = useReviewBatchJobScope();
  const addJob = useReviewBatchJobsStore((state) => state.addJob);
  return useCallback(
    (batch: TransferReviewBatch, targetIds: string[]) => {
      if (!batch.id || !scope) return;
      addJob({ id: batch.id, ...scope, targetIds, submittedAt: Date.now() });
    },
    [addJob, scope]
  );
};
